import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { onBoardingService } from 'app/modules/landing/onBoarding/onBoarding.service';
import { Subject, take, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        // NgClass,
        MatDividerModule,
        TranslocoModule
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    private _profileImageLoaded: boolean = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private translocoService: TranslocoService,
        private _authService: AuthService,
        private _onBoardingService: onBoardingService
    ) {}

  changeLanguage(lang: string) {
    this.translocoService.setActiveLang(lang);
  }


    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                
                // Only fetch profile image once
                if (!this._profileImageLoaded && user) {
                    this._profileImageLoaded = true;
                    
                    // Call onBoarding service List method to get profile image - using take(1) to complete after first emission
                    this._onBoardingService.List()
                        .pipe(take(1))
                        .subscribe({
                            next: (response) => {
                                if (response && response.profileURLImage && (!this.user.avatar || this.user.avatar !== response.profileURLImage)) {
                                    // Update user with profile image
                                    this.user = {
                                        ...this.user,
                                        avatar: response.profileURLImage
                                    };
                                    
                                    // Update user service
                                    this._userService.update(this.user).pipe(take(1)).subscribe();
                                    
                                    // Mark for check to update the UI
                                    this._changeDetectorRef.markForCheck();
                                }
                            },
                            error: (error) => {
                                console.error('Error fetching onBoarding profile:', error);
                            }
                        });
                }

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService
            .update({
                ...this.user,
            })
            .subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._authService.rememberMe = false;
        this._router.navigate(['/sign-out']);
    }
}
